import React, { useEffect, useMemo, useState } from 'react';

export interface AutocompleteOption {
  value: string;
  label: string;
}

export interface AutocompleteDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  allowCustom?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  onCommit?: () => void;
  onHoverOption?: (value: string | null) => void;
  disabled?: boolean;
  /** The input's accessible name where no visible label sits beside it. */
  ariaLabel?: string;
  /** The text of the list's first row, which selects ''. */
  emptyLabel?: string;
  /** Tab still picks the typed match or the arrowed row, then lets focus move on, as a
   *  native select does. Without it, Tab keeps focus in the field. */
  tabMovesFocus?: boolean;
}

const AutocompleteDropdown: React.FC<AutocompleteDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select or type...',
  allowCustom = true,
  inputRef: inputRefProp,
  onCommit,
  onHoverOption,
  disabled = false,
  ariaLabel,
  emptyLabel = '-- None --',
  tabMovesFocus = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  /** The row ArrowDown and ArrowUp reached: 0 is the empty row, then one per filtered option. */
  const [highlightedRow, setHighlightedRow] = useState<number | null>(null);
  const localInputRef = React.useRef<HTMLInputElement>(null);
  const inputRef = inputRefProp ?? localInputRef;
  const containerRef = React.useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter(opt =>
      opt.value.toLowerCase().includes(lower) ||
      opt.label.toLowerCase().includes(lower)
    );
  }, [options, search]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        onHoverOption?.(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) setHighlightedRow(null);
  }, [isOpen]);

  const handleSelect = (val: string) => {
    onChange(val);
    setSearch('');
    setIsOpen(false);
    onHoverOption?.(null);
  };

  // Show the label (not the raw value) when the dropdown is closed
  const displayValue = useMemo(() => {
    if (!value) return '';
    const match = options.find(o => o.value === value);
    return match ? match.label : value;
  }, [value, options]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          ref={inputRef}
          aria-label={ariaLabel}
          value={isOpen ? search : displayValue}
          onChange={(e) => {
            setSearch(e.target.value);
            setHighlightedRow(null);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => { if (!disabled) setIsOpen(true); }}
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              // An open list takes the Escape, so the editor around it stays open.
              if (isOpen) e.preventDefault();
              setIsOpen(false);
              setSearch('');
              onHoverOption?.(null);
              return;
            }
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              if (disabled) return;
              const lastRow = filteredOptions.length;
              setIsOpen(true);
              setHighlightedRow((row) => (e.key === 'ArrowDown'
                ? Math.min(lastRow, row === null ? 0 : row + 1)
                : Math.max(0, row === null ? 0 : row - 1)));
              return;
            }
            if (e.key !== 'Enter' && e.key !== 'Tab') return;
            if (e.key === 'Tab' && e.shiftKey) return;
            if (e.key === 'Enter' || !tabMovesFocus) e.preventDefault();

            const query = search.trim();
            if (isOpen && highlightedRow !== null) {
              handleSelect(highlightedRow === 0 ? '' : filteredOptions[highlightedRow - 1].value);
            } else if (isOpen && query) {
              if (filteredOptions.length) {
                handleSelect(filteredOptions[0].value);
              } else if (allowCustom) {
                handleSelect(query);
              } else {
                setIsOpen(false);
                setSearch('');
              }
            } else {
              setIsOpen(false);
              setSearch('');
            }

            onCommit?.();
          }}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: 8,
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            fontSize: 12,
          }}
        />
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 4,
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxHeight: 200,
          overflowY: 'auto',
          zIndex: 1000,
        }}>
          <div
            onClick={() => handleSelect('')}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: 11,
              color: '#9ca3af',
              background: highlightedRow === 0 ? '#e5e7eb' : value === '' ? '#f3f4f6' : 'transparent',
              borderBottom: '1px solid #f3f4f6',
            }}
          >
            {emptyLabel}
          </div>
          {filteredOptions.map((opt, idx) => (
            <div
              key={idx}
              onClick={() => handleSelect(opt.value)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 11,
                background: highlightedRow === idx + 1 ? '#e5e7eb' : value === opt.value ? '#dbeafe' : 'transparent',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; onHoverOption?.(opt.value); }}
              onMouseLeave={(e) => { e.currentTarget.style.background = value === opt.value ? '#dbeafe' : 'transparent'; onHoverOption?.(null); }}
            >
              <div style={{ fontWeight: 500 }}>{opt.label}</div>
              {opt.label !== opt.value && (
                <div style={{ fontSize: 10, color: '#6b7280' }}>{opt.value}</div>
              )}
            </div>
          ))}
          {allowCustom && search && !filteredOptions.some(o => o.value === search) && (
            <div
              onClick={() => handleSelect(search)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 11,
                background: '#f0fdf4',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <div style={{ fontWeight: 500, color: '#166534' }}>Use "{search}"</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Custom value</div>
            </div>
          )}
          {!allowCustom && search && filteredOptions.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: 11, color: '#ef4444' }}>
              No matching option found. Select from the list.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteDropdown;
